import { Empty, Toast } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { CustomViewMonthCalendarBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    CustomViewConfig,
    FilterCommonCondition,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewControlPaginateToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { ViewAppendParams } from '@lighthouse/shared'
import {
    findRecordInPool,
    getCursorStyle,
    getPaddingStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    MONTH_CALENDAR_PAGE_SIZE,
    scrollToRecord,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useLanguageContext,
    useRegisterViewControlListeners,
    ViewBlockTool
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { addDays, addMonths, eachWeekOfInterval, endOfDay, endOfMonth, startOfDay, startOfMonth } from 'date-fns'
import { useStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { fetchViewRecordsAtom } from '@/atoms/blockRecordsDict/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import * as srv from '@/services'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useGroup } from '../hooks/useGroup'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useQuickFilter } from '../hooks/useQuickFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useSubscribeView } from '../hooks/useSubscribeView'
import { useTableProps } from '../hooks/useTableProps'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { CustomControllerProps } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const CustomMonthCalendarController: React.FC<CustomControllerProps> = ({ blockData, node, firstRef, onReady, children, ...rest }) => {
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        linkFilterController,
        quickFilter,
        groupConfigure,
        canGroup,
        canSearch,
        triggerWorkflow,
        importMode,
        repeat,
        compareFields,
        breakPoint: baseBreakpoint,
        toolbar,
        recordOperator,
        filter,
        calendarStartDateField,
        calendarEndDateField,
        cursor
    } = config as CustomViewConfig

    const envId = useCurrentEnvId()

    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, record?.id, scope])

    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)

    const { quickFiltersRule, handleQuickFilter } = useQuickFilter({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        quickFilter
    })

    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    const { tablePropsCache, handleDisplayChange } = useTableProps({
        appId,
        envId,
        blockId: uniqueId
    })

    const { initGroup, tabValue, tabList, handleTabChange, getFilterByTab, handleGroupChange, handleGetGroupRecordCount } = useGroup({
        appId,
        envId,
        blockId: uniqueId,
        dataSource,
        canGroup,
        groupConfigure
    })

    const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(Date.now()))

    const getFilterByCalendarDate = useCallback(() => {
        if (!calendarStartDateField || !calendarEndDateField) {
            return
        }

        const monthStart = startOfMonth(currentMonth)
        const monthEnd = endOfMonth(currentMonth)
        // The calendar grid renders Mon-Sun weeks that can extend into the
        // previous/next month. Expand the filter to the full visible range so
        // those out-of-month days also fetch their records.
        const calendarWeeks = eachWeekOfInterval({ start: monthStart, end: monthEnd }, { weekStartsOn: 1 })
        const calendarRangeStart = startOfDay(calendarWeeks[0])
        const calendarRangeEnd = endOfDay(addDays(calendarWeeks[calendarWeeks.length - 1], 6))
        return [
            {
                idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: calendarStartDateField } },
                operator: '<=',
                paramList: [{ type: VariableType.VALUE, valueVariable: { value: calendarRangeEnd.valueOf(), type: 'date' } }]
            },
            {
                idVariable: { type: VariableType.FIELD_ID, fieldIdVariable: { fieldId: calendarEndDateField } },
                operator: '>=',
                paramList: [{ type: VariableType.VALUE, valueVariable: { value: calendarRangeStart.valueOf(), type: 'date' } }]
            }
        ] as FilterCommonCondition[]
    }, [calendarStartDateField, calendarEndDateField, currentMonth])

    const {
        searchVal,
        currentPage = 1,
        rowTotal,
        recordPoints,
        handleSearch
    } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })

    const viewIndependentDataOmitRecord = useMemo(
        () => ({
            dsId: pointer,
            currentPage,
            rowTotal,
            search: searchVal
        }),
        [currentPage, pointer, rowTotal, searchVal]
    )

    const { convertTextByLanguage } = useLanguageContext()

    const allRichtext = useMemo(() => {
        const toolbarRich = toolbar?.map(item => item.name) || []
        const recordOperatorRich = recordOperator?.map(item => item.name) || []

        return [...toolbarRich, ...recordOperatorRich, baseBreakpoint.emptyView?.title].filter(Boolean) as RichTextContentProtocol[]
    }, [toolbar, recordOperator, baseBreakpoint.emptyView?.title])
    const handleRenderRichText = useRenderRichText(allRichtext)

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { ref, width: blockWidth, breakPoint } = useBreakpoint([!!fetchViewRecordsValue])

    const stableDeps = useFetchViewDataDeps({
        quickFiltersRule,
        resolvedFilter,
        resolvedLinkFilter
    })

    const fetchViewData = useCallback(async () => {
        const noGroupOrHasTab = !initGroup.groupByFieldId || (initGroup.groupByFieldId && tabValue)
        const quickFiltersWithMonth = getFilterByCalendarDate()
        if (!quickFiltersWithMonth || !noGroupOrHasTab) {
            return
        }
        const quickFiltersWithTab: FilterCommonCondition[] = [
            ...(stableDeps.quickFiltersRule || []),
            ...quickFiltersWithMonth,
            ...getFilterByTab(tabValue)
        ]

        await mutation({
            stackId,
            pageId,
            viewId: id,
            uniqueId,
            scope,
            isSyncComponent: !!scope,
            pagination: { currentPage: 1, pageSize: MONTH_CALENDAR_PAGE_SIZE },
            params: {
                quickFilters: quickFiltersWithTab,
                linkFilter: stableDeps.resolvedLinkFilter,
                filter: stableDeps.resolvedFilter
            },
            search: searchVal,
            disableFetchCount: true
        })
    }, [
        initGroup.groupByFieldId,
        tabValue,
        getFilterByCalendarDate,
        stableDeps.quickFiltersRule,
        stableDeps.resolvedLinkFilter,
        stableDeps.resolvedFilter,
        getFilterByTab,
        mutation,
        stackId,
        pageId,
        id,
        uniqueId,
        scope,
        searchVal
    ])

    const getGroupRecordCount = useCallback(
        () =>
            handleGetGroupRecordCount({
                quickFilters: stableDeps.quickFiltersRule,
                filter: stableDeps.resolvedFilter,
                linkFilter: stableDeps.resolvedLinkFilter,
                search: searchVal
            }),
        [handleGetGroupRecordCount, stableDeps.quickFiltersRule, stableDeps.resolvedFilter, stableDeps.resolvedLinkFilter, searchVal]
    )

    const handleAction = useCallback(
        (id: string, actorEvent: ActorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger, record]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    useConfigure({
        blockData,
        fetchViewData,
        getGroupRecordCount
    })

    useSubscribeView({ pointer, fetchViewData, pageNum: currentPage })

    const quickFilterData = useMemo(() => {
        return {
            mode: quickFilter?.mode || 'normal',
            rules: quickFiltersRule
        }
    }, [quickFilter?.mode, quickFiltersRule])

    const handleViewRefresh = useCallback(() => {
        fetchViewData()
    }, [fetchViewData])

    const store = useStore()

    useRegisterViewControlListeners(id, scope, 'view', {
        refresh: handleViewRefresh,
        paginateTo: (payload: ViewControlPaginateToAction['params'] | undefined) => {
            const paginateToType = payload?.paginateToType

            if (!paginateToType) {
                return
            }

            const step = paginateToType === 'next' ? 1 : -1

            setCurrentMonth(s => addMonths(s, step))
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    const handleAppend = useCallback(
        async (params: ViewAppendParams) => {
            const isAppend = await srv.appendDataToDataSource({
                ...params,
                sheetDto: {
                    ...params.sheetDto,
                    envId,
                    triggerWorkflow,
                    repeat,
                    compareFields,
                    importMode
                }
            })
            isAppend && Toast.success(convertTextByLanguage('importSuccess'))
            fetchViewData()
            return isAppend
        },
        [envId, triggerWorkflow, repeat, compareFields, importMode, convertTextByLanguage, fetchViewData]
    )

    return useMemo(() => {
        if (!pointer || !dataSource) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_datasource.svg')} alt="请在「右侧栏>配置」中选择数据来源" />}
                    description="请在「右侧栏>配置」中选择数据来源"
                />
            )
        }

        if (!calendarStartDateField || !calendarEndDateField) {
            return (
                <Empty
                    style={{ padding: 32 }}
                    icon={<EmptyImage src={getAssetUrl('empty', 'no_content.svg')} alt="请在「右侧栏>配置」中选择开始和结束时间字段" />}
                    description="请在「右侧栏>配置」中选择开始和结束时间字段"
                />
            )
        }

        if (firstRef.current && !fetchViewRecordsValue) {
            return null
        }
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: baseBreakpoint.size.overflow,
                    ...rest.style,
                    ...(viewType === 'custom'
                        ? { ...getPaddingStyle(baseBreakpoint.viewLayout?.viewPadding, true), ...getCursorStyle(appId, cursor) }
                        : {})
                }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockTool
                            appId={appId}
                            envId={envId}
                            uniqueId={uniqueId}
                            dataSource={dataSource}
                            dataSourceList={dataSourceList}
                            tabList={tabList}
                            tabValue={tabValue}
                            blockData={blockData}
                            quickFilterData={quickFilterData}
                            viewIndependentDataOmitRecord={viewIndependentDataOmitRecord}
                            groupCache={initGroup}
                            tablePropsCache={tablePropsCache}
                            onTabChange={handleTabChange}
                            onSearch={handleSearch}
                            onFilter={handleQuickFilter}
                            onChangeDisplay={handleDisplayChange}
                            onChangeGroup={handleGroupChange}
                            onAppend={handleAppend}
                            onToolbarActionTrigger={handleAction}
                            onRenderButtonTitle={handleRenderRichText}
                        >
                            <ViewBlockProvider
                                uniqueId={uniqueId}
                                blockData={blockData}
                                dataSource={dataSource}
                                recordPoints={recordPoints}
                                recordPoolAtom={recordPoolAtom}
                                dataSourceList={dataSourceList}
                            >
                                <CustomViewMonthCalendarBlock
                                    currentMonth={currentMonth}
                                    node={node}
                                    blockWidth={blockWidth}
                                    breakPoint={breakPoint}
                                    onRenderRichText={handleRenderRichText}
                                    onRecordClick={handleRecordClickEvent}
                                />
                            </ViewBlockProvider>
                        </ViewBlockTool>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        calendarStartDateField,
        calendarEndDateField,
        firstRef,
        fetchViewRecordsValue,
        recordPoints,
        id,
        title,
        viewType,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        baseBreakpoint.viewLayout?.viewPadding,
        rest,
        appId,
        cursor,
        ref,
        breakPoint,
        envId,
        uniqueId,
        dataSourceList,
        tabList,
        tabValue,
        blockData,
        quickFilterData,
        viewIndependentDataOmitRecord,
        initGroup,
        tablePropsCache,
        handleTabChange,
        handleSearch,
        handleQuickFilter,
        handleDisplayChange,
        handleGroupChange,
        handleAppend,
        handleAction,
        handleRenderRichText,
        currentMonth,
        node,
        blockWidth,
        handleRecordClickEvent,
        children
    ])
}

export default CustomMonthCalendarController
